import type { ApiClient } from "@/modules/api/apiClient.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { queryOptions, type QueryKey } from "@tanstack/react-query";
import type { MetalSpec, MetalSpecListItem } from "@warrenio/api-spec/spec.oats.gen";
import { mapFromEntries } from "@warrenio/utils/collections/maps";
import { atomFamily } from "jotai-family";

export interface MetalSpecListItemLoc extends MetalSpecListItem {
    location: string;
}

type Response = Map<MetalSpec["uuid"], MetalSpecListItemLoc>;

function getQueryKey(params?: Params): QueryKey {
    return params == null ? ["metal/spec"] : ["metal/spec", params.location];
}

function specFromData(data: MetalSpecListItem, location: string): MetalSpecListItemLoc {
    return { ...data, location };
}

interface Params {
    location: string;
}

function getQuery(client: ApiClient, params: Params) {
    return queryOptions({
        queryKey: getQueryKey(params),
        queryFn: async ({ signal }): Promise<Response> => {
            const { location } = params;
            return mapFromEntries(
                getResponseData(
                    await client.GET("/{location}/metal/specs", {
                        signal,
                        params: { path: { location } },
                    }),
                ),
                (spec) => [spec.uuid, specFromData(spec, location)] as const,
            );
        },
    });
}

export const specLocationQueryAtom = atomFamily((location: string) =>
    atomFromStandardQueryOptions(getQuery, { location }),
);
